import { FixStatus, PrMergeStatus } from './fix';

export type FindingStatus = 'open' | 'resolved' | 'ignored' | 'false_positive' | 'needs_manual_review';
export type FindingSeverity = 'critical' | 'high' | 'medium' | 'low';
export type DismissedBy = 'ai' | 'manual';

/** Reason a person gave when they set the finding's current status. */
export interface FindingStatusReason {
  reason: string;
  status: FindingStatus;
  actor_user_id: string;
  created_at: string;
}

/** Matches the backend's FINDING_STATUS_REASON_MAX_LENGTH. */
export const FINDING_STATUS_REASON_MAX_LENGTH = 1000;

// Fix summary for list views
export interface FindingFixSummary {
  fix_id: string;
  fix_status: FixStatus;
  has_pr: boolean;
  pr_status?: PrMergeStatus;
}

// Assessment types
export type AssessmentStatus = 'pending' | 'in_progress' | 'completed' | 'failed';
export type RealConcern = 'Yes' | 'Yes, but low impact' | 'No' | 'Unclear – code not found';
export type AssessedPriority = 'High' | 'Medium' | 'Low';
export type ShouldFix = 'Yes' | 'No' | 'Nice-to-have';
export type CodeAtFault = 'Yes' | 'No' | 'N/A';
export type PracticalExploitability = 'Very low' | 'Low' | 'Medium' | 'High';
export type CVSSSeverity = 'None' | 'Low' | 'Medium' | 'High' | 'Critical';

export interface Finding {
  id: string;
  fingerprint: string;
  application_id: string;
  organization_id: string;
  repository_provider?: string;
  repository_instance?: string;
  repository_full_name?: string;
  branch?: string;
  check_id: string;
  file_path: string;
  severity: FindingSeverity;
  message: string;
  title?: string;
  category: string;
  vulnerability_class: string[];
  cwe: string[];
  owasp: string[];
  status: FindingStatus;
  dismissed_by?: DismissedBy;
  status_reason?: FindingStatusReason | null;
  first_detected_at: string;
  last_detected_at: string;
  resolved_at?: string;
  references: string[];
  source_url?: string;
  scan_type: string;
  created_at: string;
  updated_at: string;

  // Assessment fields
  assessment_status?: AssessmentStatus;
  is_real_concern?: RealConcern;
  assessed_priority?: AssessedPriority;
  should_fix?: ShouldFix;
  is_code_at_fault?: CodeAtFault;
  practical_exploitability?: PracticalExploitability;
  assessment_notes?: string;
  assessed_at?: string;
  duplicate_of_finding_id?: string;

  // CVSS 3.1 fields
  cvss_attack_vector?: string;
  cvss_attack_complexity?: string;
  cvss_privileges_required?: string;
  cvss_user_interaction?: string;
  cvss_scope?: string;
  cvss_confidentiality?: string;
  cvss_integrity?: string;
  cvss_availability?: string;
  cvss_vector?: string;
  cvss_base_score?: number;
  cvss_severity?: CVSSSeverity;

  // Fix summary (from list endpoint)
  fix_summary?: FindingFixSummary;
}

export interface FindingOccurrence {
  id: string;
  finding_id: string;
  scan_id: string;
  line_start: number;
  line_end: number;
  col_start: number;
  col_end: number;
  commit_hash?: string;
  branch: string;
  detected_at: string;
}

export interface FindingListResponse {
  findings: Finding[];
  total: number;
  limit: number;
  skip: number;
  has_next: boolean;
}

/** The file's own lines around a finding, as they were at the scanned commit. */
export interface FindingSnippet {
  file_path: string;
  line_start: number;
  line_end: number;
  /** The cited lines plus a few lines of context either side. */
  code: string;
  /** Line number of the first line in `code`. */
  code_start_line: number;
  /** True: quote found on the cited lines. False: found elsewhere or not at all. Null: no quote. */
  verified: boolean | null;
  truncated: boolean;
}

export interface FindingEvidence {
  finding_id: string;
  scan_id: string;
  commit_hash?: string | null;
  snippet: FindingSnippet;
  updated_at: string;
}

export interface FindingEvidenceResponse {
  evidence: FindingEvidence | null;
}

export interface FindingOccurrenceListResponse {
  occurrences: FindingOccurrence[];
  total: number;
}

export interface FindingStatsResponse {
  total: number;
  by_severity: {
    critical: number;
    high: number;
    medium: number;
    low: number;
  };
  by_status: {
    open: number;
    resolved: number;
    ignored: number;
    false_positive: number;
    needs_manual_review: number;
  };
}

export interface FindingStatusUpdateRequest {
  status: FindingStatus;
  reason?: string;
}

// Assessment API response types
export interface FindingAssessmentResponse {
  finding_id: string;
  status?: FindingStatus;
  dismissed_by?: DismissedBy;
  assessment_status?: AssessmentStatus;
  is_real_concern?: RealConcern;
  assessed_priority?: AssessedPriority;
  should_fix?: ShouldFix;
  is_code_at_fault?: CodeAtFault;
  practical_exploitability?: PracticalExploitability;
  assessment_notes?: string;
  assessed_at?: string;
  duplicate_of_finding_id?: string;
}

export interface AssessmentTriggerResponse {
  finding_id: string;
  task_id: string;
  status: string;
}

export interface BatchAssessmentTriggerResponse {
  scan_id: string;
  assessment_id: string;
  task_id: string;
  status: string;
  findings_count: number;
}

// Batch assessment status types
export type BatchAssessmentStatus = 'queued' | 'running' | 'completed' | 'failed';

export interface BatchAssessment {
  id: string;
  scan_id: string;
  status: BatchAssessmentStatus;
  total_findings: number;
  assessed_count: number;
  failed_count: number;
  created_at: string;
  started_at?: string;
  completed_at?: string;
  error_message?: string;
}

// Vulnerability trend types
export interface VulnerabilityTrendPoint {
  date: string;
  new_count: number;
  resolved_count: number;
  net_open: number;
}

export interface VulnerabilityTrendSummary {
  total_new: number;
  total_resolved: number;
  net_change: number;
  current_open: number;
}

export interface VulnerabilityTrendWindow {
  start: string;
  end: string;
  days: number;
}

export interface VulnerabilityTrendResponse {
  organization_id: string;
  window: VulnerabilityTrendWindow;
  trend: VulnerabilityTrendPoint[];
  summary: VulnerabilityTrendSummary;
}
